import { Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranscriptionModels, TranscriptionPayload } from 'src/app/shared/components/recorders/audio-recorder/audio-recorder.dto';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ContentStateService } from '../../../content-state.service';
import { Chunk } from '../../chunk.dto';
import { ChunkService } from '../../chunk.service';

@Component({
  selector: 'app-transcribe-video-option',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, TranslateModule, MatProgressSpinnerModule],
  template: `
    <div class="interactive-item" (click)="transcribeVideo()">
      @if (isLoading()) {
        <mat-spinner diameter="20" class="interactive-item__icon"></mat-spinner>
      } @else {
        <mat-icon class="interactive-item__icon" aria-hidden="true">subtitles</mat-icon>
      }
      <span class="interactive-item__text">
        {{ 'ChunkController.Options.TranscribeVideo' | translate }}
      </span>
    </div>
  `,
})
export class TranscribeVideoOptionComponent {
  @Input() chunk!: Chunk;
  @Input() contentID = '';
  @Output() actionTriggered = new EventEmitter<void>();

  private chunkService = inject(ChunkService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private confirmService = inject(AppConfirmService);
  private contentStateService = inject(ContentStateService);

  isLoading = signal(false);

  transcribeVideo() {
    if (this.isLoading()) return;

    this.confirmService
      .confirm({
        title: this.translate.instant('ChunkController.Options.TranscribeVideo'),
        message: this.translate.instant('ChunkController.Options.TranscribeVideoConfirm'),
      })
      .subscribe(async (confirmed) => {
        if (confirmed) {
          this.isLoading.set(true);
          const contentId = this.contentID || (this.chunk as any).contentID || '';
          const fileLink = this.chunk?.editions?.find((ed) => ed.language === this.translate.currentLang)?.content || this.chunk?.editions?.[0]?.content;

          if (!fileLink) {
            this.isLoading.set(false);
            this.toast.snackbar.open('No video file link found.', 'Ok', { duration: 3000 });
            return;
          }

          // Show loader and update local chunk status
          this.contentStateService.setLoading({
            status: 'loading',
            message: this.translate.instant('Content.Chunk.TranscriptionInProgress'),
            reason: 'transcribe-audio',
          });

          const targetChunk = { ...this.chunk, status: 'processing' };
          this.contentStateService.updateChunkLocally(this.chunk._id, targetChunk);
          this.actionTriggered.emit(); // Close overlay

          try {
            const transcriptionData: TranscriptionPayload = {
              fileLink,
              language: this.translate.currentLang,
              settings: { model: TranscriptionModels.CHIRP3 },
              chunkId: this.chunk._id,
              contentID: contentId,
            };

            await this.chunkService.transcribeAudio(transcriptionData);
            this.contentStateService.setLoading({
              status: 'success',
              message: this.translate.instant('ChunkController.Options.TranscribeVideoStarted') || 'Transcription started in background',
              reason: 'transcribe-audio',
            });
          } catch (err) {
            console.error('Video transcription initiation failed:', err);
            // Restore status locally
            const restoredChunk = { ...this.chunk, status: 'ready' };
            this.contentStateService.updateChunkLocally(this.chunk._id, restoredChunk);
            this.contentStateService.setLoading({
              status: 'error',
              message: this.translate.instant('Content.Chunk.TranscriptionFailed'),
              reason: 'transcribe-audio',
            });
          } finally {
            this.isLoading.set(false);
            setTimeout(() => this.contentStateService.setLoading({ status: 'idle' }), 2000);
          }
        }
      });
  }
}
